<script setup lang="ts">
// Risk tab -> "Live vs backtest": is the user's live trading tracking the backtest? A pill row picks
// FOF (the whole shared pot vs the backtest of the same allocation; GET /api/risk/pot-parity) or one
// fund (that fund's live exposure vs its backtest; GET /api/risk/pot-parity?fund=ID, fund list from
// /api/risk/pot-parity/funds). The selection persists in localStorage and is the same on every fund page.
// Chart = RiskLineChart (the Risk tab's lightweight-charts line component); it owns no loading toggle,
// so the fetch here guards the mount with `loading`.
import { ref, computed, onMounted } from 'vue'
import { fundDisplayLabel } from './pricingSourceHelpers'
import { api, type PotParityResponse, type PotParityMetrics, type PotParityFundEntry } from '../api/client'
import { ordinal } from './potParityFormat'
import RiskLineChart, { type RiskChartSeries, type RiskChartMarker } from './RiskLineChart.vue'
import EdgeSourceStack from './edge/EdgeSourceStack.vue'

const data = ref<PotParityResponse | null>(null)
const loading = ref(true)
const error = ref('')
const showMismatches = ref(false)

const FUND_KEY = 'sw.risk.parity.fund'
const funds = ref<PotParityFundEntry[]>([])
const selected = ref('FOF')
// Bumped on every fetch so a slow response for a previously selected fund cannot overwrite the current one.
let fetchSeq = 0

function readStored(): string {
  try { return localStorage.getItem(FUND_KEY) || 'FOF' } catch { return 'FOF' }
}
function writeStored(v: string) {
  try { localStorage.setItem(FUND_KEY, v) } catch { /* storage unavailable */ }
}

async function load() {
  const seq = ++fetchSeq
  loading.value = true
  error.value = ''
  try {
    const d = selected.value === 'FOF' ? await api.potParity() : await api.potParity(selected.value)
    if (seq === fetchSeq) data.value = d
  } catch (e) {
    if (seq === fetchSeq) { data.value = null; error.value = e instanceof Error ? e.message : String(e) }
  } finally {
    if (seq === fetchSeq) loading.value = false
  }
}

async function select(id: string) {
  if (id === selected.value) return
  selected.value = id
  writeStored(id)
  await load()
}

// Edge-source history (spec 2026-10-30 §6–§7): a fund view shows the source-of-edge stack under Growth of 1,
// whatever pot parity says (it reads its own endpoint). The edge map moved to Strategy -> Edge (2026-10-31); the
// window (120 / 250 decision days) is shared with it and persisted per browser.
const EDGE_WINDOW_KEY = 'sw.risk.edge.window'
function readEdgeWindow(): 120 | 250 {
  try { return localStorage.getItem(EDGE_WINDOW_KEY) === '120' ? 120 : 250 } catch { return 250 }
}
const edgeWindow = ref<120 | 250>(readEdgeWindow())

function setEdgeWindow(w: 120 | 250) {
  if (w === edgeWindow.value) return
  edgeWindow.value = w
  try { localStorage.setItem(EDGE_WINDOW_KEY, String(w)) } catch { /* storage unavailable */ }
}

onMounted(async () => {
  try {
    const list = await api.potParityFunds()
    funds.value = Array.isArray(list) ? list : []
  } catch { funds.value = [] }
  const stored = readStored()
  selected.value = stored !== 'FOF' && funds.value.some(f => f.fundId === stored) ? stored : 'FOF'
  await load()
})

const isFund = computed(() => selected.value !== 'FOF')

// Fund view: liveHeld says which side held the fund (the backtest held the opposite).
function fundMismatchText(m: { date: string; liveHeld?: boolean | null }): string {
  if (m.liveHeld === true) return `${m.date}: live in, backtest out`
  if (m.liveHeld === false) return `${m.date}: live out, backtest in`
  return `${m.date}: live and backtest differed`
}

const STATUS_COPY: Record<string, string> = {
  not_on_pot: "You're not on the shared pot",
  no_live_trading: 'No live pot trading yet',
  not_computed: 'First comparison runs tonight',
  not_traded: 'Not traded live yet',
}
const VERDICT_COPY: Record<string, string> = {
  on_track: 'On track',
  diverging: 'Diverging',
  below_expectation: 'Below expectation',
  too_short: 'Too few sessions to judge',
}

const statusMessage = computed(() => (data.value && data.value.status !== 'ok' ? STATUS_COPY[data.value.status] ?? data.value.status : ''))
const isOk = computed(() => data.value?.status === 'ok')
const verdictText = computed(() => {
  const d = data.value
  if (!d) return ''
  if (d.tooShort) return VERDICT_COPY.too_short
  return d.verdict ? VERDICT_COPY[d.verdict] ?? d.verdict : ''
})
const verdictClass = computed(() => {
  const d = data.value
  if (!d || d.tooShort) return 'v-grey'
  return d.verdict === 'on_track' ? 'v-ok' : d.verdict === 'diverging' ? 'v-warn' : d.verdict === 'below_expectation' ? 'v-bad' : 'v-grey'
})

const dash = '—'
const pct = (v: number | null | undefined, dp = 1) => (v == null ? dash : `${(v * 100).toFixed(dp)}%`)
const num = (v: number | null | undefined, dp = 2) => (v == null ? dash : v.toFixed(dp))
const ptile = (v: number | null | undefined) => (v == null ? '' : `${ordinal(Math.round(v))} percentile of backtest windows`)

interface Tile { key: string; title: string; live: string; bt: string | null; sub: string; recent: string }
const tiles = computed<Tile[]>(() => {
  const m: PotParityMetrics | null = data.value?.sinceInception ?? null
  const r: PotParityMetrics | null = data.value?.last20 ?? null
  if (!m) return []
  return [
    { key: 'cagr', title: 'CAGR', live: pct(m.liveCagr), bt: `backtest ${pct(m.btCagr)}`, sub: ptile(data.value?.liveCagrPctile), recent: `last 20: ${pct(r?.liveCagr)}` },
    { key: 'sharpe', title: 'Sharpe', live: num(m.liveSharpe), bt: `backtest ${num(m.btSharpe)}`, sub: ptile(data.value?.liveSharpePctile), recent: `last 20: ${num(r?.liveSharpe)}` },
    { key: 'corr', title: 'Correlation', live: num(m.correlation), bt: null, sub: 'daily live vs backtest', recent: `last 20: ${num(r?.correlation)}` },
    { key: 'te', title: 'Tracking error', live: pct(m.trackingError), bt: null, sub: `cumulative gap ${num(m.cumulativeGapPct, 1)} pts`, recent: `last 20: ${pct(r?.trackingError)}` },
  ]
})

// Short on-chart labels ("Alloc", "JH preset"); the full text is in the list below the chart.
const chartMarkers = computed<RiskChartMarker[]>(() => (data.value?.markers ?? []).map(m => ({
  time: m.date,
  text: m.kind === 'allocation' ? 'Alloc' : m.label.split(' →')[0],
  muted: m.reconstructed,
})))
const anyReconstructed = computed(() => (data.value?.markers ?? []).some(m => m.reconstructed))

const chartSeries = computed<RiskChartSeries[]>(() => {
  const s = data.value?.series ?? []
  return [
    { id: 'live', label: 'Live', color: '#4caf50', lineWidth: 2, data: s.map(p => ({ time: p.date, value: p.liveIndex })) },
    // Live days after the last backtest day carry btIndex null; lightweight-charts plots null as 0, so drop them.
    { id: 'bt', label: 'Backtest', color: '#42a5f5', lineWidth: 1, dashed: true,
      data: s.flatMap(p => (p.btIndex == null ? [] : [{ time: p.date, value: p.btIndex }])) },
  ]
})
</script>

<template>
  <div class="parity" data-testid="pot-parity-view">
    <div class="parity-fund-row" data-testid="parity-fund-row">
      <button type="button" class="parity-pill" :class="{ active: selected === 'FOF' }" :aria-pressed="selected === 'FOF'"
              data-testid="parity-fund-FOF" @click="select('FOF')">FOF</button>
      <button v-for="f in funds" :key="f.fundId" type="button" class="parity-pill" :class="{ active: selected === f.fundId }"
              :aria-pressed="selected === f.fundId" :data-testid="`parity-fund-${f.fundId}`" @click="select(f.fundId)">{{ fundDisplayLabel(f.fundId) }}</button>
    </div>
    <p v-if="loading" class="parity-muted">Loading live vs backtest...</p>
    <p v-else-if="error" class="parity-error" data-testid="parity-error">Could not load the comparison: {{ error }}</p>

    <template v-else-if="data">
      <p v-if="!isOk" class="parity-status" data-testid="parity-status">{{ statusMessage }}</p>
      <p v-if="!isOk && data.liveScopeNote" class="parity-note" data-testid="parity-scope-note">{{ data.liveScopeNote }}</p>

      <template v-if="isOk">
        <p class="parity-header" data-testid="parity-header">Since {{ data.inception }} · {{ data.sessions }} sessions · backtest data to {{ data.lastBacktestDay }}</p>
        <p v-if="data.compareNote" class="parity-note" data-testid="parity-compare-note">{{ data.compareNote }}</p>

        <div class="parity-tiles">
          <div v-for="t in tiles" :key="t.key" class="parity-tile" :class="{ 'is-grey': data.tooShort }" :data-testid="`parity-tile-${t.key}`">
            <div class="pt-title">{{ t.title }}</div>
            <div class="pt-live">{{ t.live }}</div>
            <div v-if="t.bt" class="pt-sub">{{ t.bt }}</div>
            <div v-if="t.sub" class="pt-sub">{{ t.sub }}</div>
            <div class="pt-sub">{{ t.recent }}</div>
          </div>
        </div>

        <p class="parity-verdict" :class="verdictClass" data-testid="parity-verdict">
          {{ verdictText }}
          <span v-if="data.lowConfidence" class="parity-tag" data-testid="parity-low-confidence">low confidence</span>
        </p>

        <div v-if="data.series.length > 1" class="parity-chart" data-testid="parity-chart">
          <h3 class="parity-chart-title">Growth of 1 (live vs backtest)</h3>
          <RiskLineChart :key="selected" :series="chartSeries" :markers="chartMarkers" format="number2" />
        </div>
        <EdgeSourceStack v-if="isFund" :key="selected" :fund-id="selected" :window="edgeWindow" @window="setEdgeWindow" />

        <p v-if="anyReconstructed" class="parity-note" data-testid="parity-reconstructed-note">Changes marked as reconstructed are rebuilt from past events — best effort.</p>
        <ul v-if="data.markers.length" class="parity-markers">
          <li v-for="(m, i) in data.markers" :key="i" data-testid="parity-marker" :class="{ 'is-reconstructed': m.reconstructed }">
            <span class="pm-date">{{ m.date }}</span> {{ m.label }}
            <span v-if="m.reconstructed" class="parity-tag">reconstructed</span>
          </li>
        </ul>

        <div class="parity-mismatches" data-testid="parity-mismatches">
          <button type="button" class="parity-toggle" :aria-expanded="showMismatches" @click="showMismatches = !showMismatches">
            Holding mismatches ({{ data.mismatches.length }})
            <span>{{ showMismatches ? '▾' : '▸' }}</span>
          </button>
          <ul v-if="showMismatches">
            <li v-for="(m, i) in data.mismatches" :key="i">{{ isFund ? fundMismatchText(m) : `${m.date} · ${m.fundId}` }}</li>
            <li v-if="!data.mismatches.length" class="parity-muted">None</li>
          </ul>
        </div>

        <p v-if="data.liveScopeNote" class="parity-note" data-testid="parity-scope-note">{{ data.liveScopeNote }}</p>
      </template>
    </template>

    <template v-if="!loading && !isOk && isFund">
      <EdgeSourceStack :key="selected" :fund-id="selected" :window="edgeWindow" @window="setEdgeWindow" />
    </template>
  </div>
</template>

<style scoped>
.parity { display: flex; flex-direction: column; gap: 0.75rem; }
.parity-fund-row { display: flex; gap: 0.4rem; overflow-x: auto; white-space: nowrap; }
.parity-pill { flex: none; cursor: pointer; background: #1a1a1a; border: 1px solid #333; border-radius: 16px; color: #ccc; font: inherit; font-size: 0.8rem; padding: 0.3rem 0.8rem; min-height: 44px; }
.parity-pill.active { border-color: #4caf50; color: #fff; }
.parity-muted { color: #888; }
.parity-error { color: #ef5350; }
.parity-status { color: #ccc; font-size: 1rem; }
.parity-note { color: #999; font-size: 0.8rem; margin: 0; }
.parity-header { color: #aaa; font-size: 0.85rem; margin: 0; }
.parity-tiles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
@media (min-width: 769px) { .parity-tiles { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
.parity-tile { background: #1a1a1a; border: 1px solid #333; border-left: 3px solid #4caf50; border-radius: 6px; padding: 0.75rem; min-width: 0; }
.parity-tile.is-grey { border-left-color: #666; opacity: 0.55; }
.pt-title { color: #aaa; font-size: 0.75rem; text-transform: uppercase; }
.pt-live { font-size: 1.4rem; font-weight: 600; color: #eee; }
.pt-sub { color: #888; font-size: 0.72rem; overflow-wrap: anywhere; }
.parity-verdict { font-size: 1rem; font-weight: 600; margin: 0; }
.v-ok { color: #4caf50; }
.v-warn { color: #ffb74d; }
.v-bad { color: #ef5350; }
.v-grey { color: #999; }
.parity-tag { margin-left: 0.4rem; padding: 0.05rem 0.4rem; border: 1px solid #555; border-radius: 10px; color: #aaa; font-size: 0.68rem; font-weight: 400; }
.parity-chart { background: #1a1a1a; border: 1px solid #333; border-radius: 6px; padding: 0.75rem; }
.parity-chart-title { margin: 0 0 0.5rem; font-size: 0.9rem; color: #ccc; }
.parity-markers { list-style: none; margin: 0; padding: 0; font-size: 0.8rem; color: #ccc; display: flex; flex-direction: column; gap: 0.25rem; }
.pm-date { color: #888; margin-right: 0.35rem; }
.parity-toggle { cursor: pointer; margin: 0; width: 100%; background: none; border: 0; padding: 0; font: inherit; font-size: 0.9rem; font-weight: 600; color: #ccc; min-height: 44px; display: flex; align-items: center; justify-content: space-between; }
.is-reconstructed { color: #888; font-style: italic; }
.parity-mismatches ul { margin: 0; padding-left: 1rem; font-size: 0.8rem; color: #ccc; }
</style>
